import { useEffect, useRef, useState } from 'react';
import { useCommandBarEmployeeSearchLazyQuery } from '../../__generated__/graphql';
import { CommandItem } from './types';
import { routes } from '../../routes';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

type RecordSearchResult = {
  items: CommandItem[];
  isLoading: boolean;
};

export function useRecordSearch(query: string): RecordSearchResult {
  const [items, setItems] = useState<CommandItem[]>([]);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const [searchEmployees, { loading: employeesLoading }] =
    useCommandBarEmployeeSearchLazyQuery({
      fetchPolicy: 'cache-first',
    });

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (query.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setIsDebouncing(false);
      return;
    }

    setIsDebouncing(true);

    timerRef.current = setTimeout(async () => {
      setIsDebouncing(false);
      const { data } = await searchEmployees({
        variables: { searchTerm: query },
      });

      const employeeItems: CommandItem[] = (data?.activeEmployees ?? []).map(
        (emp) => ({
          id: `record-employee-${emp.Id}`,
          type: 'record' as const,
          label: emp.Name,
          description: 'Employee',
          icon: 'person-outline',
          keywords: [],
          category: 'Employees',
          route:
            routes.peopleDirectory.route.template() + `?employeeId=${emp.Id}`,
        }),
      );

      setItems(employeeItems);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [query, searchEmployees]);

  return {
    items,
    isLoading: isDebouncing || employeesLoading,
  };
}
